import React, { useCallback, useState } from 'react'
import { useSelector } from 'react-redux'
import clsx from 'classnames'
import Countdown, { type CountdownRenderProps } from 'react-countdown'
import moment from 'moment'
import { APIService } from 'api/APIService'
import { getSelectedCityId } from 'src/store/app.state.store'
import { getCities } from 'src/store/data.store'
import { useObject } from 'src/hooks/useObject'
import { cleanPhone } from 'utils/cleanPhone'
import { validateEmail } from 'utils/validateEmail'
import { codeInputMask, focusMaskStart } from 'utils/masks'
import { ShadowInput } from 'components/FormElements/ShadowInput/ShadowInput'
import { ShadowSelect } from 'components/FormElements/ShadowSelect/ShadowSelect'
import { AppButton } from 'components/FormElements/AppButton/AppButton'
import { StatusModal } from 'components/Utility/StatusModal/StatusModal'
import { AppLink } from 'components/Utility/AppLink/AppLink'
import { usePhoneField } from '../usePhoneField'
import formStyles from './SignUpForm.module.scss'
import styles from '../Form.module.scss'

const SEX_OPTIONS = [{ id: 2, name: 'Мужской' }, { id: 3, name: 'Женский' }]

export type SignUpResult = { phone: string, password: string }

type SignUpFormProps = {
  onSuccess: (result: SignUpResult) => void,
}

type TextField = 'firstName' | 'lastName' | 'email' | 'password' | 'passwordConfirm'

const pad = (value: number) => value.toString().padStart(2, '0')

/** Registration (#signup): client data, then the SMS code */
export const SignUpForm: React.FC<SignUpFormProps> = ({ onSuccess }) => {
  const cities = useSelector(getCities)
  const selectedCityId = useSelector(getSelectedCityId)
  const [formData, setFormField] = useObject({
    defaultCityId: selectedCityId,
    firstName: '',
    lastName: '',
    sex: null as number | null,
    phone: '',
    email: '',
    password: '',
    passwordConfirm: '',
    personalData: true,
  })
  const [code, setCode] = useState('')
  /** When a new code may be requested, from the API */
  const [reclaimAt, setReclaimAt] = useState<string | null>(null)
  const [stage, setStage] = useState<'data' | 'confirm'>('data')
  const [formError, setFormError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const setPhone = useCallback((value: string) => setFormField('phone', value), [setFormField])
  const phoneField = usePhoneField(formData.phone, setPhone)
  const phone = cleanPhone(formData.phone)
  const { password, passwordConfirm } = formData
  const passwordMismatch = !!(password.length && passwordConfirm.length && password !== passwordConfirm)

  const sendCode = useCallback(async () => {
    try {
      const { expires_at: expiresAt } = await APIService.auth.confirmPhone(phone, 'sign-up')
      setReclaimAt(expiresAt)
    } catch (e) {
      setFormError((e as Error).message)
    }
  }, [phone])

  const validateData = () => {
    if (!formData.defaultCityId) {
      return 'Не выбран город'
    }
    if (!formData.firstName) {
      return 'Не указано имя'
    }
    if (!phone) {
      return 'Не указан номер телефона'
    }
    if (formData.email.length && !validateEmail(formData.email)) {
      return 'Неверный формат email'
    }
    if (!password) {
      return 'Не указан пароль'
    }
    if (passwordMismatch) {
      return 'Пароли не совпадают'
    }
    return null
  }

  const submitData = async () => {
    const error = validateData()
    if (error) {
      setFormError(error)
      return
    }
    setLoading(true)
    try {
      await APIService.auth.signUpCheck({ ...formData, defaultCityId: formData.defaultCityId ?? undefined, phone })
      await sendCode()
      setStage('confirm')
    } catch (err) {
      setFormError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }

  const submitCode = async () => {
    if (!code) {
      setFormError('Не указан код подтверждения')
      return
    }
    setLoading(true)
    try {
      await APIService.auth.signUp({
        ...formData,
        defaultCityId: formData.defaultCityId as number,
        birthday: null,
        phone,
        code,
      })
      setLoading(false)
      onSuccess({ phone, password })
    } catch (err) {
      setFormError((err as Error).message)
      setLoading(false)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (stage === 'data') {
      submitData()
    } else {
      submitCode()
    }
  }

  const renderCountdown = ({ minutes, seconds, completed }: CountdownRenderProps) => {
    if (completed) {
      return <span onClick={(e) => {
        e.preventDefault()
        sendCode()
      }}>Получить код повторно</span>
    }
    return <span>Запросить код повторно можно через {pad(minutes)}:{pad(seconds)}</span>
  }

  const dismissError = useCallback(() => setFormError(null), [])

  const textField = (
    field: TextField,
    placeholder: string,
    shadowColor: string,
    extra: Partial<React.ComponentProps<typeof ShadowInput>> = {},
  ) => (
    <ShadowInput
      autoComplete={'new-client-' + field.replace(/[A-Z]/g, (c) => '-' + c.toLowerCase())}
      onChange={(value) => setFormField(field, value)}
      value={formData[field]}
      className={styles.field}
      shadowColor={shadowColor}
      placeholder={placeholder}
      {...extra}
    />
  )

  return (
    <form noValidate onSubmit={handleSubmit} className={styles.form}>
      {stage === 'data' && (
        <div className={styles.fields}>
          <ShadowSelect
            onChange={(value) => setFormField('defaultCityId', value)}
            value={formData.defaultCityId ?? ''}
            className={styles.field}
            shadowColor={'yellow'}
            placeholder={'Город*'}
            options={cities.map(({ city }) => city)}
          />
          {textField('firstName', 'Имя*', 'pink')}
          {textField('lastName', 'Фамилия', 'pink')}
          <ShadowSelect
            onChange={(value) => setFormField('sex', value)}
            value={formData.sex ?? ''}
            className={styles.field}
            shadowColor={'blue'}
            placeholder={'Пол'}
            options={SEX_OPTIONS}
          />
          <ShadowInput
            {...phoneField}
            autoComplete={'new-client-phone'}
            className={styles.field}
            shadowColor={'satin'}
            placeholder={'Мобильный телефон*'}
          />
          {textField('email', 'Email', 'satin', { type: 'email' })}
          {textField('password', 'Пароль*', 'purple', { lang: 'en', type: 'password', error: passwordMismatch })}
          {textField('passwordConfirm', 'Повторите пароль*', 'purple', {
            lang: 'en', type: 'password', error: passwordMismatch,
          })}
          <div className={clsx([styles.field, styles.personal])}>
            <div>
              <span>
                Нажимая на кнопку &quot;Готово&quot; я даю своё согласие на обработку моей персональной
                информации на условиях, определенных{' '}
              </span>
              <AppLink href={'/personal'} target={'_blank'} rel="noopener noreferrer">
                Политикой конфидициальности.
              </AppLink>
            </div>
          </div>
        </div>
      )}
      {stage === 'confirm' && (
        <div className={styles.fields}>
          <div className={styles.note}>
            Введите код из смс
          </div>
          <ShadowInput
            onFocus={focusMaskStart}
            mask={codeInputMask}
            onChange={setCode}
            value={code}
            className={styles.field}
            shadowColor={'purple'}
          />
          <div className={formStyles.resend}>
            <Countdown
              key={reclaimAt ?? undefined}
              date={moment(reclaimAt ?? undefined).toDate()}
              renderer={renderCountdown}
            />
          </div>
        </div>
      )}
      <div className={styles.actions}>
        <AppButton type={'submit'} disabled={loading} className={styles.action}>
          Готово
        </AppButton>
      </div>
      <StatusModal
        status={'error'}
        modalText={formError}
        buttonText={'ОК'}
        onClose={dismissError}
        onButtonClick={dismissError}
        isOpen={formError !== null}
      />
    </form>
  )
}
